/*
 * Copyright ©2020. Open Digital Solutions, Novi Sad. Sva prava zadržana.
 * Pravo da se koristi, kopira, modifikuje i distribuira ovaj softver i njegova dokumentacija
 * u bilo koje svrhe, bez naknade ili bez potpisanog sporazuma sa vlasnikom softvera, nije dozvoljeno.
 */

import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LocalStorageService } from 'src/app/local-storage.service';
import { environment } from 'src/environments/environment';
import { AppService } from 'src/services/app.service';
import { DataSharingService } from 'src/services/data-sharing.service';
import { OciService } from 'src/services/oci.service';
import { VsService } from 'src/services/vs.service';
import * as XLSX from 'xlsx';

interface DocumentData {
   document: any;
   action: 'cancel';
   subjectId: any;
   mode?: 'accounting' | 'default';
}
@Component({
   selector: 'preview-document',
   templateUrl: './preview-document.component.html',
   styleUrls: ['./preview-document.component.scss'],
})
export class PreviewDocumentComponent implements OnInit {
   signDocumentForm: FormGroup = new FormGroup({});
   data: any;
   docUrl;
   requireSignature = false;
   // isPdf = false;
   // isDoc = false;
   // isXlsx = false;
   // isTxt = false;
   time: any;
   loggedIn: any;
   fileType: string;

   excelData: any[][] = [];
   txtContent: string | null = null;

   constructor(
      public dialogRef: MatDialogRef<any>,
      @Optional() @Inject(MAT_DIALOG_DATA) public document: DocumentData & { requireSignature: boolean },
      private appService: AppService,
      private formBuilder: FormBuilder,
      private dataSharingService: DataSharingService,
      private vsService: VsService,
      private ociService: OciService,
      private localStorageService: LocalStorageService,
   ) {
      this.loggedIn = JSON.parse(this.localStorageService.get('loggedUser'));
   }

   ngOnInit(): void {
      this.data = this.document.document;
      this.time = this.data.dateOfModification;
      this.requireSignature = this.document.requireSignature;
      const isAccountingPreview = this.document?.mode === 'accounting';

      this.fileType = this.getFileType(this.data.format);
      this.docUrl = this.appService.getDocManagerUrl() + environment.ociViewDocument + this.document.document.documentId;
      if (this.fileType === 'xlsx') {
         this.loadExcel(this.docUrl);
      } else if (this.fileType === 'txt') {
         fetch(this.docUrl, { credentials: 'include' })
            .then(res => res.text())
            .then(text => (this.txtContent = text));
      }

      this.signDocumentForm = this.formBuilder.group({
         note: ['', []],
      });
   }

   getFileType(format: string): string {
      if (format === 'application/pdf') {
         return 'pdf';
      } else if (format === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
         return 'docx';
      } else if (format === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
         return 'xlsx';
      } else if (format === 'text/plain') {
         return 'txt';
      }
      return 'unknown';
   }

   handleSignature(type: any) {
      const note = this.signDocumentForm.get('note').value;
      const handleSignatureDto = {
         documentId: this.document.document.documentId,
         caseId: this.data.caseId,
         caseName: this.data.caseName,
         status: type,
         documentName: this.document.document.name,
         documentNote: note,
      };
      // olja.adjelovski
      // potpisivanje dokumenta (Odobravanje)
      if (this.document.document.name.includes('VS-ZAKLJUCAK')) {
         this.vsService.supervisorDecisionMake(type === 'accept', this.data.caseId).subscribe(() => {
            this.ociService.handleSignature(handleSignatureDto).subscribe(res => {
               this.dialogRef.close({ event: 'sign-document', data: note, typeOfDocument: 'VS-ZAKLJUCAK' });
            });
         });
      } else {
         this.ociService.handleSignature(handleSignatureDto).subscribe(res => {
            this.dialogRef.close({
               event: type === 'accept' ? 'sign-document' : 'reject-document',
               data: note,
               typeOfDocument: this.document.document.name.includes('VS-PP') ? 'VS-PP' : 'VS-PU',
            });
         });
      }
   }

   closeDialog() {
      this.dialogRef.close({ event: 'cancel' });
   }

   loadExcel(fileUrl: string) {
      fetch(fileUrl, { credentials: 'include' })
         .then(res => res.arrayBuffer())
         .then(buffer => {
            const workbook = XLSX.read(buffer, {
               type: 'array',
               cellDates: true,
            });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            this.excelData = XLSX.utils
               .sheet_to_json(sheet, {
                  header: 1,
                  raw: true,
               })
               .map((row: any[], rowIndex: number) => {
                  if (rowIndex === 0) return row; // preskoci header

                  return row.map(cell => {
                     if (cell instanceof Date) {
                        return new Intl.DateTimeFormat('sr-RS').format(cell); // format datuma
                     }

                     if (typeof cell === 'number') {
                        // ako broj ima decimalni dio
                        if (!Number.isInteger(cell)) {
                           return cell.toLocaleString('sr-RS', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                           });
                        }

                        // ako je cijeli broj
                        return cell.toLocaleString('sr-RS', { maximumFractionDigits: 0 });
                     }

                     // sve ostalo vrati bez formatiranja
                     return cell;
                  });
               });
         });
   }

   isDate(value: any): boolean {
      console.log(value, value instanceof Date);
      return value instanceof Date;
   }

   parseBackendDate(raw: any): Date {
      if (!raw) return null;

      if (raw instanceof Date) {
         return raw;
      }

      const str = String(raw).trim();

      if (!isNaN(Date.parse(str))) {
         return new Date(str);
      }

      const parts = str.match(/\d+/g);

      if (!parts || parts.length < 6) {
         console.error('Invalid datetime:', str);
         return null;
      }

      const [day, month, year, hours, minutes, seconds] = parts.map(Number);

      return new Date(year, month - 1, day, hours, minutes, seconds);
   }
}
/**
 * TODO @gorandivovic @jelenaninkovic @danijelgrabez @oljaandjelovski
 *
 * 1. Connect to API and retrieve correct document for preview
 * 2. Add proper type for Document shape
 */
